import type { Anchor, Block, Chapter, Highlight, Note } from '../shared/types';
import type { BookDetail } from './api';
import { initBar } from './floatbar';
import { toast } from './toast';

export interface SelectionInfo {
  text: string;
  anchor: Anchor | null; // 跨段选区为 null
  rect: DOMRect;
}

export interface ReaderHooks {
  getBook(): BookDetail | null;
  getChapter(): Chapter | null;
  getHighlights(): Highlight[];
  getNotes(): Note[];
  chapterIndex(): number;
  goChapter(index: number, blockId?: string | null): Promise<void>;
  savePosition(blockId: string | null): void;
  addHighlight(anchor: Anchor): Promise<void>;
  removeHighlight(id: string): Promise<void>;
  onAsk(sel: SelectionInfo): void;
  onAnnotate(anchor: Anchor | null, quote: string): void;
  /** 作者身份显示名：'me' → 我，模型 id → 模型名 */
  authorName(author: string): string;
}

interface ResolvedRange {
  id: string;
  blockId: string;
  start: number;
  end: number;
}

const FORMAT_LABEL: Record<string, string> = { epub: 'EPUB', txt: 'TXT', md: 'MARKDOWN' };

let hooks: ReaderHooks;
let currentSel: SelectionInfo | null = null;
let floatbar: ReturnType<typeof initBar>;
let markbar: ReturnType<typeof initBar>;
let activeMark: { id: string; anchor: Anchor } | null = null;

function el(id: string): HTMLElement {
  return document.getElementById(id)!;
}

/** 锚点上下文快照长度（字符） */
const ANCHOR_CONTEXT = 32;

/** 在章节内重新定位锚点：偏移校验 → 全章搜原文（多个命中用 prefix/suffix 消歧） */
function resolveAnchor(anchor: Anchor, chapter: Chapter): ResolvedRange | null {
  const byId = chapter.blocks.find((b) => b.id === anchor.blockId);
  if (byId && byId.text.slice(anchor.start, anchor.end) === anchor.exact) {
    return { id: '', blockId: byId.id, start: anchor.start, end: anchor.end };
  }
  let best: ResolvedRange | null = null;
  let bestScore = -1;
  for (const b of chapter.blocks) {
    let from = 0;
    while (from < b.text.length) {
      const i = b.text.indexOf(anchor.exact, from);
      if (i < 0) break;
      let score = b.id === anchor.blockId ? 1 : 0;
      if (anchor.prefix && b.text.slice(Math.max(0, i - anchor.prefix.length), i) === anchor.prefix) score += 2;
      if (anchor.suffix && b.text.slice(i + anchor.exact.length, i + anchor.exact.length + anchor.suffix.length) === anchor.suffix) score += 2;
      if (score > bestScore) {
        bestScore = score;
        best = { id: '', blockId: b.id, start: i, end: i + anchor.exact.length };
      }
      from = i + 1;
    }
  }
  return best;
}

function blockElement(range: Range, edge: 'start' | 'end'): HTMLElement | null {
  const node = edge === 'start' ? range.startContainer : range.endContainer;
  const element = node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : node.parentElement;
  return element?.closest('[data-block-id]') ?? null;
}

function offsetInBlock(blockEl: HTMLElement, node: Node, offset: number): number {
  const walker = document.createTreeWalker(blockEl, NodeFilter.SHOW_TEXT);
  let total = 0;
  let cur = walker.nextNode();
  while (cur) {
    if (cur === node) return total + offset;
    total += cur.textContent?.length ?? 0;
    cur = walker.nextNode();
  }
  if (node.nodeType === Node.ELEMENT_NODE) {
    let sum = 0;
    for (let i = 0; i < offset; i++) sum += node.childNodes[i]?.textContent?.length ?? 0;
    return sum;
  }
  return total;
}

function captureSelection(): SelectionInfo | null {
  const sel = window.getSelection();
  const chapter = hooks.getChapter();
  if (!sel || sel.isCollapsed || sel.rangeCount === 0 || !chapter) return null;
  const range = sel.getRangeAt(0);
  const text = (sel.toString() ?? '').replace(/\s+/g, ' ').trim();
  if (!text) return null;

  const startEl = blockElement(range, 'start');
  const endEl = blockElement(range, 'end');
  if (!startEl || !endEl) return null;

  let anchor: Anchor | null = null;
  if (startEl === endEl) {
    const a = offsetInBlock(startEl, range.startContainer, range.startOffset);
    const b = offsetInBlock(startEl, range.endContainer, range.endOffset);
    const [start, end] = a <= b ? [a, b] : [b, a];
    const blockText = startEl.textContent ?? '';
    anchor = {
      chapterId: chapter.id,
      blockId: startEl.dataset['blockId']!,
      start,
      end,
      exact: blockText.slice(start, end),
      prefix: blockText.slice(Math.max(0, start - ANCHOR_CONTEXT), start),
      suffix: blockText.slice(end, end + ANCHOR_CONTEXT),
    };
  }
  return { text, anchor, rect: range.getBoundingClientRect() };
}

function appendWithMarks(target: HTMLElement, text: string, ranges: ResolvedRange[]) {
  let pos = 0;
  for (const r of ranges) {
    // Nested highlights must not move the cursor backwards and duplicate prose.
    if (r.end <= pos) continue;
    const start = Math.max(pos, r.start);
    if (start > pos) target.append(document.createTextNode(text.slice(pos, start)));
    const mark = document.createElement('mark');
    mark.dataset['hlId'] = r.id;
    mark.textContent = text.slice(start, r.end);
    target.append(mark);
    pos = r.end;
  }
  if (pos < text.length) target.append(document.createTextNode(text.slice(pos)));
}

function blockNode(block: Block): HTMLElement {
  const tag = block.kind === 'h1' || block.kind === 'h2' ? 'h2' : block.kind === 'h3' ? 'h3' : 'p';
  const node = document.createElement(tag);
  if (block.kind === 'quote') node.className = 'bq';
  node.dataset['blockId'] = block.id;
  return node;
}

export function renderAll() {
  const column = el('column');
  const book = hooks.getBook();
  const chapter = hooks.getChapter();
  column.innerHTML = '';

  if (!book) {
    column.innerHTML = `
      <div class="empty-book">
        <div class="glyph">‖</div>
        <div class="line1">Margin</div>
        <div class="line2 ui">从左侧导入一本 EPUB / TXT / Markdown，或在书库中选一本书</div>
      </div>`;
    el('readerFoot').hidden = true;
    el('crumb').textContent = 'Margin';
    return;
  }
  if (!chapter) return;

  const overline = document.createElement('div');
  overline.className = 'overline ui';
  overline.textContent = book.title;
  const title = document.createElement('h1');
  title.className = 'chapter-title';
  title.textContent = chapter.title;
  const subtitle = document.createElement('div');
  subtitle.className = 'subtitle ui';
  subtitle.textContent = `${book.author} · ${FORMAT_LABEL[book.format] ?? book.format}`;
  column.append(overline, title, subtitle);

  // 解析本章划线与批注位置
  const rangesByBlock = new Map<string, ResolvedRange[]>();
  let lost = 0;
  for (const h of hooks.getHighlights()) {
    if (h.anchor.chapterId !== chapter.id) continue;
    const r = resolveAnchor(h.anchor, chapter);
    if (!r) { lost++; continue; }
    r.id = h.id;
    const list = rangesByBlock.get(r.blockId) ?? [];
    list.push(r);
    rangesByBlock.set(r.blockId, list);
  }
  const notesByBlock = new Map<string, Note[]>();
  for (const n of hooks.getNotes()) {
    if (!n.anchor || n.anchor.chapterId !== chapter.id) continue;
    const r = resolveAnchor(n.anchor, chapter);
    if (r) {
      const list = notesByBlock.get(r.blockId) ?? [];
      list.push(n);
      notesByBlock.set(r.blockId, list);
    } else lost++;
  }
  if (lost > 0) console.info(`[margin] ${lost} 条划线/批注未能重新定位`);

  const article = document.createElement('article');
  for (const block of chapter.blocks) {
    const node = blockNode(block);
    const ranges = (rangesByBlock.get(block.id) ?? []).sort((a, b) => a.start - b.start);
    appendWithMarks(node, block.text, ranges);
    if (notesByBlock.has(block.id) && (block.kind === 'p' || block.kind === 'quote')) {
      const dot = document.createElement('span');
      dot.className = 'anchor-dot';
      node.prepend(dot);
    }
    article.append(node);
    // 块下方的批注卡片（人 = ● 墨色，AI = ✦ 强调色）
    for (const note of notesByBlock.get(block.id) ?? []) article.append(noteCard(note));
  }
  column.append(article);

  // 面包屑与页脚
  el('crumb').innerHTML = '';
  el('crumb').append(document.createTextNode(`${book.title} — `), bold(chapter.title));
  el('readerFoot').hidden = false;
  const index = hooks.chapterIndex();
  el('prevCh').classList.toggle('disabled', index <= 0);
  el('nextCh').classList.toggle('disabled', index >= book.chapters.length - 1);
  (el('prevCh') as HTMLButtonElement).disabled = index <= 0;
  (el('nextCh') as HTMLButtonElement).disabled = index >= book.chapters.length - 1;
  updateStatus();
}

function bold(text: string): HTMLElement {
  const b = document.createElement('b');
  b.textContent = text;
  return b;
}

function shortTime(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 正文块下方的批注卡片：作者徽标 + 作者名 + 文本 + 时间 */
function noteCard(note: Note): HTMLElement {
  const card = document.createElement('div');
  card.className = 'note-card';

  const head = document.createElement('div');
  head.className = 'nc-head ui';
  const ai = note.author !== 'me';
  const badge = document.createElement('span');
  badge.className = `nc-badge ${ai ? 'ai' : 'human'}`;
  badge.textContent = ai ? '✦' : '●';
  head.append(
    badge,
    document.createTextNode(`${hooks.authorName(note.author)}`),
  );
  const time = document.createElement('span');
  time.className = 'nc-time';
  time.textContent = shortTime(note.updatedAt);
  head.append(time);

  const text = document.createElement('div');
  text.className = 'nc-text';
  text.textContent = note.text;

  card.append(head, text);
  return card;
}

function updateStatus() {
  const book = hooks.getBook();
  if (!book) return;
  const page = el('page');
  const max = page.scrollHeight - page.clientHeight;
  const pct = max > 0 ? Math.min(100, Math.round((page.scrollTop / max) * 100)) : 100;
  el('pageStatus').textContent = `第 ${hooks.chapterIndex() + 1} / ${book.chapters.length} 章 · ${pct}%`;
  (el('progressBar') as HTMLElement).style.width = `${pct}%`;
}

let scrollTimer: ReturnType<typeof setTimeout> | null = null;
let selStableTimer: ReturnType<typeof setTimeout> | null = null;
function onScroll() {
  updateStatus();
  if (scrollTimer) clearTimeout(scrollTimer);
  scrollTimer = setTimeout(() => {
    const page = el('page');
    const top = page.getBoundingClientRect().top;
    let first: HTMLElement | null = null;
    for (const node of document.querySelectorAll<HTMLElement>('#column [data-block-id]')) {
      if (node.getBoundingClientRect().bottom > top + 90) { first = node; break; }
    }
    hooks.savePosition(first?.dataset['blockId'] ?? null);
  }, 250);
}

export function scrollToBlock(blockId: string | null) {
  if (!blockId) return;
  const node = document.querySelector(`[data-block-id="${blockId}"]`);
  node?.scrollIntoView({ block: 'start' });
}

export async function jumpToAnchor(anchor: Anchor) {
  const chapter = hooks.getChapter();
  const book = hooks.getBook();
  if (!chapter || !book) return;
  const go = () => {
    const resolved = resolveAnchor(anchor, hooks.getChapter()!);
    if (!resolved) {
      toast('原文位置已变化');
      return;
    }
    const node = document.querySelector(`[data-block-id="${resolved.blockId}"]`);
    node?.scrollIntoView({ block: 'center' });
    node?.classList.add('flash');
    setTimeout(() => node?.classList.remove('flash'), 1700);
  };
  if (anchor.chapterId !== chapter.id) {
    const index = book.chapters.findIndex((c) => c.id === anchor.chapterId);
    if (index < 0) {
      toast('原文位置已变化');
      return;
    }
    await hooks.goChapter(index, null);
    // The user may have navigated elsewhere while the chapter was loading.
    if (hooks.getBook()?.id !== book.id || hooks.getChapter()?.id !== anchor.chapterId) return;
    go();
  } else {
    go();
  }
}

async function onBarAction(act: string) {
  const sel = currentSel;
  if (!sel) return;
  floatbar.hide();
  if (act === 'ask') hooks.onAsk(sel);
  else if (act === 'note') hooks.onAnnotate(sel.anchor, sel.text);
  else if (act === 'hl' && sel.anchor) {
    await hooks.addHighlight(sel.anchor);
    window.getSelection()?.removeAllRanges();
    currentSel = null;
    renderAll();
  }
}

async function onMarkAction(act: string) {
  const mark = activeMark;
  markbar.hide();
  if (!mark) return;
  if (act === 'del') {
    await hooks.removeHighlight(mark.id);
    renderAll();
  } else if (act === 'note') {
    hooks.onAnnotate(mark.anchor, mark.anchor.exact);
  }
}

export function initReader(h: ReaderHooks) {
  hooks = h;
  floatbar = initBar('floatbar', (act) => void onBarAction(act));
  markbar = initBar('markbar', (act) => void onMarkAction(act));

  const column = el('column');

  column.addEventListener('mouseup', () => {
    setTimeout(() => {
      const sel = captureSelection();
      currentSel = sel;
      if (sel) {
        floatbar.show(sel.rect, sel.anchor ? [] : ['hl']);
        hooks.onSelectionChange?.(sel);
      } else {
        floatbar.hide();
      }
    }, 0);
  });

  document.addEventListener('selectionchange', () => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) {
      if (floatbar.visible()) {
        floatbar.hide();
        currentSel = null;
      }
      return;
    }
    // 触屏端没有可靠的 mouseup：选区稳定后再捕获
    if (selStableTimer) clearTimeout(selStableTimer);
    selStableTimer = setTimeout(() => {
      const info = captureSelection();
      if (info) {
        currentSel = info;
        floatbar.show(info.rect, info.anchor ? [] : ['hl']);
        hooks.onSelectionChange?.(info);
      }
    }, 320);
  });

  column.addEventListener('click', (e) => {
    const mark = (e.target as HTMLElement).closest('mark') as HTMLElement | null;
    if (!mark) { markbar.hide(); return; }
    const chapter = hooks.getChapter();
    const highlight = hooks.getHighlights().find((hl) => hl.id === mark.dataset['hlId']);
    if (!chapter || !highlight) return;
    activeMark = { id: highlight.id, anchor: highlight.anchor };
    markbar.show(mark.getBoundingClientRect());
  });

  el('page').addEventListener('scroll', onScroll);
  el('prevCh').addEventListener('click', () => hooks.goChapter(hooks.chapterIndex() - 1));
  el('nextCh').addEventListener('click', () => hooks.goChapter(hooks.chapterIndex() + 1));

  document.addEventListener('keydown', (e) => {
    const target = e.target as HTMLElement;
    if (e.defaultPrevented || e.isComposing || ['TEXTAREA', 'INPUT', 'SELECT'].includes(target.tagName) || target.isContentEditable) return;
    if (document.querySelector('dialog[open], .app.shelf-open, .app.companion-open') || !el('displayPanel').hidden || !el('authMask').hidden) return;
    if (e.key === 'ArrowLeft') hooks.goChapter(hooks.chapterIndex() - 1);
    if (e.key === 'ArrowRight') hooks.goChapter(hooks.chapterIndex() + 1);
  });

  document.addEventListener('mousedown', (e) => {
    if (markbar.visible() && !el('markbar').contains(e.target as Node)) markbar.hide();
  });
}

// ReaderHooks 的可选扩展，main 里接线
export interface ReaderHooks {
  onSelectionChange?(sel: SelectionInfo | null): void;
}
